import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Forum as ForumIcon,
  PushPin as PushPinIcon,
  Campaign as CampaignIcon
} from '@mui/icons-material';

interface Post {
  id: string;
  type: 'post';
  author_id: string;
  author_name: string;
  title: string;
  content: string;
  category: 'general' | 'transportation' | 'accommodations' | 'other';
  is_pinned: boolean;
  is_announcement: boolean;
  is_deleted: boolean;
  edited_at: string | null;
  edited_by: 'author' | 'admin' | null;
  created_at: string;
  updated_at: string;
  reply_count: number;
}

interface MessageBoardProps {
  onCreatePost?: () => void;
  onViewPost?: (postId: string) => void;
}

export const MessageBoard: React.FC<MessageBoardProps> = ({ onCreatePost, onViewPost }) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [hasMore, setHasMore] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>('');

  const API_URL = process.env.NODE_ENV === 'production'
    ? 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod'
    : 'http://localhost:3001';

  useEffect(() => {
    // Check admin status
    const authUser = localStorage.getItem('authUser');
    if (authUser) {
      try {
        const userData = JSON.parse(authUser);
        setIsAdmin(userData.role === 'admin' || userData.groups?.includes('admins'));
        setCurrentUserId(userData.sub || userData.id || '');
      } catch (err) {
        console.error('Failed to parse user data:', err);
      }
    }
    
    loadPosts();
  }, [categoryFilter, searchQuery]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      setError(null);

      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!authToken) {
        throw new Error('Not authenticated');
      }

      // Build query parameters
      const params = new URLSearchParams();
      if (categoryFilter && categoryFilter !== 'all') {
        params.append('category', categoryFilter);
      }
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      params.append('limit', '20');

      const response = await fetch(`${API_URL}/api/message-board/posts?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please log in again.');
        }
        throw new Error('Failed to load posts');
      }

      const result = await response.json();
      if (result.success) {
        setPosts(result.data.posts || []);
        setHasMore(result.data.has_more || false);
      } else {
        throw new Error(result.error || 'Failed to load posts');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadPosts();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setCategoryFilter('all');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'general': return 'default';
      case 'transportation': return 'primary';
      case 'accommodations': return 'secondary';
      case 'other': return 'info';
      default: return 'default';
    }
  };

  const getCategoryLabel = (category: string) => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  const truncateContent = (content: string, maxLength: number = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength).trim() + '...';
  };

  if (loading && posts.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2" sx={{ display: 'flex', alignItems: 'center' }}>
          <ForumIcon sx={{ mr: 1 }} />
          Message Board
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreatePost}
        >
          Create Post
        </Button>
      </Box>

      {/* Search and Filter Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search posts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClearSearch}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="transportation">Transportation</MenuItem>
                  <MenuItem value="accommodations">Accommodations</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClearFilters}
                disabled={categoryFilter === 'all' && !searchQuery}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Posts List */}
      {posts.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <ForumIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchQuery || categoryFilter !== 'all' 
                ? 'No posts found' 
                : 'No posts yet'}
            </Typography>
            <Typography color="text.secondary" paragraph>
              {searchQuery || categoryFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Be the first to start a conversation!'}
            </Typography>
            {!searchQuery && categoryFilter === 'all' && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreatePost}
              >
                Create First Post
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {posts.map((post) => (
            <Grid item xs={12} key={post.id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)'
                  },
                  ...(post.is_pinned && {
                    border: '2px solid',
                    borderColor: 'primary.main'
                  }),
                  ...(post.is_announcement && {
                    backgroundColor: 'action.hover'
                  })
                }}
                onClick={() => onViewPost && onViewPost(post.id)}
              >
                <CardContent>
                  {/* Post Header */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        {post.is_pinned && (
                          <Chip
                            icon={<PushPinIcon />}
                            label="Pinned"
                            size="small"
                            color="primary"
                          />
                        )}
                        {post.is_announcement && (
                          <Chip
                            icon={<CampaignIcon />}
                            label="Announcement"
                            size="small"
                            color="warning"
                          />
                        )}
                        <Chip
                          label={getCategoryLabel(post.category)}
                          size="small"
                          color={getCategoryColor(post.category) as any}
                        />
                      </Box>
                      <Typography variant="h6" component="h3">
                        {post.title}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Post Content Preview */}
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mb: 2 }}
                  >
                    {truncateContent(post.content)}
                  </Typography>

                  <Divider sx={{ my: 1.5 }} />

                  {/* Post Footer */}
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="body2" color="text.secondary">
                        By {post.author_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(post.created_at)}
                      </Typography>
                      {post.edited_at && (
                        <Chip
                          label={`Edited by ${post.edited_by}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Chip
                      label={`${post.reply_count} ${post.reply_count === 1 ? 'reply' : 'replies'}`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Load More */}
      {hasMore && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Button variant="outlined" onClick={handleRefresh}>
            Load More
          </Button>
        </Box>
      )}

      {/* Refresh Button */}
      {posts.length > 0 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Button variant="text" onClick={handleRefresh} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
      )}
    </Box>
  );
};
